import React, { useEffect } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import B2bRebateApi, {
  TenantRebateBackendData,
  TenantRebateTier
} from 'api/b2b/B2bRebateApi'
import OrbitSession from 'hooks/useCookies'

import { rebatePeriodOptions } from '../contants'
import { TenantDetails } from '..'
// Adjust import path as needed

function Rebate({
  tenantDetails,
  onSaveSuccess
}: {
  tenantDetails: TenantDetails
  onSaveSuccess: (string) => void
}) {
  const {
    control,
    watch,
    getValues,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      rebate: false,
      companyName: '',
      currency: '',
      rebatePeriod: rebatePeriodOptions[0],
      rebates: [{ tierName: '', from: '', to: '', percentage: '' }]
    }
  })

  const faberToken = OrbitSession.get('faberToken')
  const emailId = OrbitSession.get('email')

  // Mutation for creating rebate
  const { mutate: createRebate, isLoading: isSubmitting } = useMutation(
    (data: TenantRebateBackendData) =>
      B2bRebateApi.createRebate(data, faberToken),
    {
      onSuccess: () => {
        toast.success('Rebate created successfully')
        onSaveSuccess('#documents')
      },
      onError: (error) => {
        console.error('API Error:', error)
        toast.error('Failed to create rebate')
      }
    }
  )

  // Setting the value of company name and currency
  useEffect(() => {
    if (tenantDetails?.name) {
      setValue('companyName', tenantDetails.name)
    }
    if (tenantDetails?.currency) {
      setValue('currency', tenantDetails.currency)
    }
  }, [tenantDetails, setValue])

  const { fields, append, remove } = useFieldArray({ control, name: 'rebates' })

  const isRebate = watch('rebate')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (data: any) => {
    if (!data.rebate) {
      toast.info('Rebate is disabled')
      onSaveSuccess?.('#documents')
      return
    }
    // Transform form data to match API interface
    const tenantRebateTiers: TenantRebateTier[] = data.rebates.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rebate: any) => ({
        rebate_tier_name: rebate.tierName,
        from_amount: Number(rebate.from),
        to_amount: Number(rebate.to),
        rebate_percentage: Number(rebate.percentage),
        rebate_period: data.rebatePeriod?.value.toUpperCase()
      })
    )

    // Get the first rebate tier for the main object (assuming the API expects this structure)
    const firstTier = data.rebates[0]
    // TODO: Remove the unwanted fields from the request body
    const requestBody: TenantRebateBackendData = {
      tenant_id: tenantDetails?.tenantId || '',
      rebate_tier_name: firstTier.tierName,
      rebate_period: data.rebatePeriod?.value.toUpperCase(),
      from_amount: 2,
      to_amount: 20,
      rebate_percentage: 20,
      is_active: true,
      created_by: emailId || '',
      updated_by: emailId || '',
      tenant_rebate_tier: tenantRebateTiers
    }

    createRebate(requestBody)
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center'>
          <Controller
            name='rebate'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={field.onChange}
                  />
                }
                label='Enable Rebate'
              />
            )}
          />
        </Box>

        {isRebate && (
          <>
            <Box display='flex' alignItems='center'>
              <Typography sx={{ mr: '10px' }}>Company Name</Typography>
              <Controller
                name='companyName'
                control={control}
                render={({ field }) => (
                  <TextField
                    id='companyName'
                    variant='outlined'
                    size='small'
                    disabled={true}
                    sx={{
                      width: '20%'
                    }}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box display='flex' alignItems='center'>
              <Typography sx={{ mr: '10px' }}>Currency</Typography>
              <Controller
                name='currency'
                control={control}
                render={({ field }) => (
                  <TextField
                    id='currency'
                    variant='outlined'
                    size='small'
                    disabled={true}
                    sx={{
                      width: '10%'
                    }}
                    {...field}
                  />
                )}
              />
            </Box>

            <Box display='flex' alignItems='center'>
              <Typography sx={{ mr: '10px' }}>Rebate Period*</Typography>
              <Controller
                name='rebatePeriod'
                control={control}
                rules={{ required: { value: true, message: 'Required' } }}
                render={({ field }) => (
                  <Autocomplete
                    disablePortal
                    options={rebatePeriodOptions}
                    onChange={(_, data) => field.onChange(data)}
                    size='small'
                    sx={{ width: '150px' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        {...field}
                        error={!!errors.rebatePeriod}
                        helperText={errors.rebatePeriod?.message}
                      />
                    )}
                  />
                )}
              />
            </Box>

            <Box>
              <TableContainer sx={{ mt: '20px' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tier Name</TableCell>
                      <TableCell>From Amount</TableCell>
                      <TableCell>To Amount</TableCell>
                      <TableCell>Percentage</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields?.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Controller
                            name={`rebates.${index}.tierName`}
                            control={control}
                            rules={{ required: 'Tier name is required' }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                size='small'
                                error={!!errors.rebates?.[index]?.tierName}
                                helperText={
                                  errors.rebates?.[index]?.tierName?.message
                                }
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`rebates.${index}.from`}
                            control={control}
                            rules={{
                              required: 'From amount is required',
                              min: { value: 0, message: 'Must be 0 or greater' }
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                size='small'
                                type='number'
                                // disabled={
                                //   index !== 0 || fields.length - 1 != index
                                // }
                                error={!!errors.rebates?.[index]?.from}
                                helperText={
                                  errors.rebates?.[index]?.from?.message
                                }
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`rebates.${index}.to`}
                            control={control}
                            rules={{
                              required: 'To amount is required',
                              min: { value: 0, message: 'Must be 0 or greater' }
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                size='small'
                                type='number'
                                // disabled={fields.length - 1 != index}
                                error={!!errors.rebates?.[index]?.to}
                                helperText={
                                  errors.rebates?.[index]?.to?.message
                                }
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Controller
                            name={`rebates.${index}.percentage`}
                            control={control}
                            rules={{
                              min: {
                                value: 0,
                                message: 'Must be 0 or greater'
                              },
                              max: {
                                value: 100,
                                message: 'Must be 100 or less'
                              },
                              required: 'Percentage is required'
                            }}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                size='small'
                                type='number'
                                error={!!errors.rebates?.[index]?.percentage}
                                helperText={
                                  errors.rebates?.[index]?.percentage?.message
                                }
                              />
                            )}
                          />
                        </TableCell>
                        {fields.length - 1 === index && (
                          <TableCell>
                            <Button
                              onClick={() =>
                                append({
                                  tierName: '',
                                  from: `${
                                    Number(+getValues(`rebates.${index}.to`)) +
                                    1
                                  }`,
                                  to: '',
                                  percentage: ''
                                })
                              }
                            >
                              +
                            </Button>
                            <Button onClick={() => remove(index)}>-</Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}

        <Box sx={{ mt: '20px' }}>
          <Button
            type='submit'
            variant='contained'
            disableElevation
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default Rebate
